import React, { useEffect, useState } from "react";
import Table from "react-bootstrap/Table";
import { Modal } from "react-bootstrap";
import { Button } from "bootstrap-4-react/lib/components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoaderSpinner from "../common/Loader";
import {
    getDropshipMarginConfig,
    upsertDropshipMarginConfig,
    deleteDropshipMarginConfig,
} from "../../store/actions/dropship.action";
import { getAllSellers } from "../../store/actions/company";
import { getAppConfiguration, updateConfig } from "../../store/actions/auth";

const SHOW_MARGIN_KEY = "DROPSHIP_SHOW_MARGIN_TO_DROPSHIPPER";

export default function DropshipMarginConfig() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userDetails = useSelector((state) => state.auth.user);
    const { marginConfigs, marginConfigsStatus } = useSelector((state) => state.dropshipReducer);
    const appConfigurations = useSelector((state) => state?.auth?.configurations) || [];
    const showMarginConfig = appConfigurations.find((c) => c.configKey === SHOW_MARGIN_KEY);
    const showMarginToDropshipper = String(showMarginConfig?.configValue ?? "false").toLowerCase() === "true";

    // Pull seller list from the same Redux slice the existing AddSeller page uses (state.company.getAllSellersData)
    const sellersData = useSelector((state) => state?.company?.getAllSellersData);
    const sellerOptions = (sellersData?.Entity?.listSellerMasterOrdersEntity || []).map((s) => ({
        id: s.id,
        label: s.sellerAndCompany || s.name || s.sellerName || `Seller #${s.id}`,
    }));
    const [globalFlat, setGlobalFlat] = useState("0");
    const [globalPercent, setGlobalPercent] = useState("0");
    const [showAddModal, setShowAddModal] = useState(false);
    const [overrideSellerId, setOverrideSellerId] = useState("");
    const [overrideFlat, setOverrideFlat] = useState("0");
    const [overridePercent, setOverridePercent] = useState("0");
    const [submitting, setSubmitting] = useState(false);

    const loader = marginConfigsStatus === "PENDING";

    useEffect(() => {
        if (userDetails?.Value && !userDetails.Value.isSuperAdmin) {
            navigate("/setting");
            return;
        }
        loadConfigs();
        loadSellers();
    }, [userDetails]);

    useEffect(() => {
        const global = marginConfigs.find((c) => c.scope === "Global");
        if (global) {
            setGlobalFlat(global.flatAmount?.toString() || "0");
            setGlobalPercent(global.percentAmount?.toString() || "0");
        }
    }, [marginConfigs]);

    const loadConfigs = () => dispatch(getDropshipMarginConfig({}));

    const loadSellers = () => {
        // Same call signature the AddSeller page uses; pageSize=1000 ensures one round-trip pulls all sellers
        const setTotalPagesNoop = () => {};
        const filterData = { searchKeywords: "" };
        dispatch(getAllSellers(setTotalPagesNoop, 1000, 1, filterData));
    };

    const handleSaveGlobal = async () => {
        const flat = Number.parseFloat(globalFlat);
        const pct = Number.parseFloat(globalPercent);
        if (Number.isNaN(flat) || Number.isNaN(pct) || flat < 0 || pct < 0 || pct > 100) {
            toast.error("Invalid margin values.");
            return;
        }
        setSubmitting(true);
        try {
            const result = await upsertDropshipMarginConfig({
                scopeCompanySellerId: null,
                flatAmount: flat,
                percentAmount: pct,
            });
            if (result.data?.EntityList?.[0]?.success) {
                toast.success("Global margin updated.");
                loadConfigs();
            } else {
                toast.error(result.data?.EntityList?.[0]?.message || "Failed to save.");
            }
        } catch (e) {
            toast.error("Failed to save global margin.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddOverride = async () => {
        if (!overrideSellerId) {
            toast.error("Please select a seller.");
            return;
        }
        const flat = Number.parseFloat(overrideFlat);
        const pct = Number.parseFloat(overridePercent);
        if (Number.isNaN(flat) || Number.isNaN(pct) || flat < 0 || pct < 0 || pct > 100) {
            toast.error("Invalid margin values.");
            return;
        }
        setSubmitting(true);
        try {
            const result = await upsertDropshipMarginConfig({
                scopeCompanySellerId: Number.parseInt(overrideSellerId, 10),
                flatAmount: flat,
                percentAmount: pct,
            });
            if (result.data?.EntityList?.[0]?.success) {
                toast.success("Per-seller override saved.");
                setShowAddModal(false);
                setOverrideSellerId("");
                setOverrideFlat("0");
                setOverridePercent("0");
                loadConfigs();
            } else {
                toast.error(result.data?.EntityList?.[0]?.message || "Failed to save.");
            }
        } catch (e) {
            toast.error("Failed to save override.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleShowMargin = async (e) => {
        const next = e.target.checked;
        if (!showMarginConfig?.id) {
            toast.error("Toggle row missing in Configuration. Run V1.1.605 migration.");
            return;
        }
        setSubmitting(true);
        try {
            const result = await Promise.resolve(
                dispatch(updateConfig({
                    id: showMarginConfig.id,
                    configKey: showMarginConfig.configKey,
                    configValue: next ? "true" : "false",
                    configName: showMarginConfig.configName,
                }))
            );
            if (result?.data?.Status === 200) {
                toast.success(`Dropshipper margin breakdown ${next ? "shown" : "hidden"}.`);
                dispatch(getAppConfiguration());
            } else {
                toast.error("Failed to update toggle.");
            }
        } catch (err) {
            toast.error("Failed to update toggle.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (configId) => {
        if (!window.confirm("Remove this per-seller override? Seller will fall back to Global default.")) return;
        try {
            const result = await deleteDropshipMarginConfig({ marginConfigId: configId });
            if (result.data?.EntityList?.[0]?.success) {
                toast.success("Override removed.");
                loadConfigs();
            } else {
                toast.error(result.data?.EntityList?.[0]?.message || "Failed to delete.");
            }
        } catch (e) {
            toast.error("Failed to delete override.");
        }
    };

    const overrides = marginConfigs.filter((c) => c.scope === "PerSeller");

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    Dropshipping Margin Configuration
                    <div className="top_right_button_container">
                        <Button className="cus-primary-transparent-btn" onClick={() => navigate("/setting")}>
                            Back
                        </Button>
                    </div>
                </div>

                {(loader || submitting) && (
                    <div className="full-screen-spinner-overlay">
                        <LoaderSpinner />
                    </div>
                )}

                {/* ── Global Default ─────────────────────────────────────── */}
                <div className="main_content_table">
                    <div className="row" style={{ padding: "20px 24px", margin: 0 }}>
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <h5 style={{ marginBottom: 8 }}>Global Default Margin</h5>
                            <p style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>
                                Applied to every dropship order routed through 3PL Vision unless a per-seller
                                override exists. Total margin = Flat + (Wholesale Amount × Percent / 100).
                            </p>
                        </div>
                        <div className="col-lg-4 col-md-6 col-sm-12 mobMb-10">
                            <div>Flat Amount ($)</div>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control"
                                value={globalFlat}
                                onChange={(e) => setGlobalFlat(e.target.value)}
                            />
                        </div>
                        <div className="col-lg-4 col-md-6 col-sm-12 mobMb-10">
                            <div>Percent Amount (%)</div>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                className="form-control"
                                value={globalPercent}
                                onChange={(e) => setGlobalPercent(e.target.value)}
                            />
                        </div>
                        <div className="col-lg-4 col-md-12 col-sm-12 d-flex align-items-end mobMb-10">
                            <Button
                                className="cus-seconday-bg-btn"
                                onClick={handleSaveGlobal}
                                disabled={submitting}
                            >
                                {submitting ? "Saving..." : "Save Global"}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* ── Dropshipper Visibility Toggle ──────────────────────── */}
                <div className="main_content_table" style={{ marginTop: 16 }}>
                    <div className="row" style={{ padding: "20px 24px", margin: 0 }}>
                        <div className="col-lg-9 col-md-8 col-sm-12">
                            <h5 style={{ marginBottom: 4 }}>Show Platform Margin to Dropshipper</h5>
                            <p style={{ color: "#666", fontSize: 13, marginBottom: 0 }}>
                                Off (default) — the platform margin is baked into the wholesale price the dropshipper sees on Import. They never see the breakdown.<br />
                                On — the dropshipper sees Wholesale + Platform Margin = Total Cost as separate lines.
                            </p>
                        </div>
                        <div className="col-lg-3 col-md-4 col-sm-12 d-flex align-items-center justify-content-end mobMb-10">
                            <label className="custom-toggle" style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 0 }}>
                                <span style={{ fontWeight: 500 }}>{showMarginToDropshipper ? "On" : "Off"}</span>
                                <input
                                    type="checkbox"
                                    checked={showMarginToDropshipper}
                                    onChange={handleToggleShowMargin}
                                    disabled={submitting || !showMarginConfig?.id}
                                />
                            </label>
                        </div>
                    </div>
                </div>

                {/* ── Per-Seller Overrides ───────────────────────────────── */}
                <div className="main_content_table" style={{ marginTop: 16 }}>
                    <div
                        className="row align-items-center"
                        style={{ padding: "20px 24px 12px", margin: 0 }}
                    >
                        <div className="col-lg-8 col-md-7 col-sm-12">
                            <h5 style={{ marginBottom: 4 }}>Per-Seller Overrides</h5>
                            <p style={{ color: "#666", fontSize: 13, marginBottom: 0 }}>
                                Override the global default for specific sellers. Sellers without an
                                override fall back to the Global Default above.
                            </p>
                        </div>
                        <div className="col-lg-4 col-md-5 col-sm-12 d-flex justify-content-end mobMb-10">
                            <Button className="cus-seconday-bg-btn" onClick={() => setShowAddModal(true)}>
                                <i className="fa fa-plus mr-1" /> Add Override
                            </Button>
                        </div>
                    </div>

                    <div className="tableFixHead" style={{ padding: "0 24px 24px" }}>
                        <Table className="table table-borderless">
                            <thead>
                                <tr>
                                    <th width="6%">#</th>
                                    <th width="45%">Seller</th>
                                    <th width="18%">Flat ($)</th>
                                    <th width="18%">Percent (%)</th>
                                    <th width="13%">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!loader && overrides.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4" style={{ color: "#999" }}>
                                            No per-seller overrides. All sellers use the Global default.
                                        </td>
                                    </tr>
                                )}
                                {overrides.map((cfg, idx) => (
                                    <tr key={cfg.id}>
                                        <td>{idx + 1}</td>
                                        <td>{cfg.sellerName}</td>
                                        <td>${cfg.flatAmount?.toFixed(2)}</td>
                                        <td>{cfg.percentAmount?.toFixed(2)}%</td>
                                        <td>
                                            <Button
                                                className="cus-primary-transparent-btn"
                                                onClick={() => handleDelete(cfg.id)}
                                            >
                                                Remove
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Add Override Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add Per-Seller Override</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row">
                        <div className="col-12 mobMb-10">
                            <div>Seller</div>
                            <select
                                className="form-control"
                                value={overrideSellerId}
                                onChange={(e) => setOverrideSellerId(e.target.value)}
                            >
                                <option value="">Select a seller...</option>
                                {sellerOptions.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                            {sellerOptions.length === 0 && (
                                <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                                    No sellers available. (Loading...)
                                </div>
                            )}
                        </div>
                        <div className="col-6 mobMb-10" style={{ marginTop: 12 }}>
                            <div>Flat Amount ($)</div>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control"
                                value={overrideFlat}
                                onChange={(e) => setOverrideFlat(e.target.value)}
                            />
                        </div>
                        <div className="col-6 mobMb-10" style={{ marginTop: 12 }}>
                            <div>Percent Amount (%)</div>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                className="form-control"
                                value={overridePercent}
                                onChange={(e) => setOverridePercent(e.target.value)}
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button className="cus-primary-transparent-btn" onClick={() => setShowAddModal(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button className="cus-seconday-bg-btn" onClick={handleAddOverride} disabled={submitting}>
                        {submitting ? "Saving..." : "Save Override"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </main>
    );
}
